import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PublicIcon from '@mui/icons-material/Public';
import VideocamIcon from '@mui/icons-material/Videocam';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import MentoLogo from '@shared/ui/components/Logo/MentoLogo';
import { platformURL } from '@shared/utils/platformUrl';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../store/hooks';
import { useBookSeriesMutation, useGetBookingSeriesOptionsQuery } from './api/adminBookSeriesApi';
import { canScheduleSeries, getDatePickerDays, getSlotsForDate } from './bookingSeriesTransforms';
import type { BookingSeriesOptionsResponse, BookSeriesResponse } from './types';

const weekdayLabels = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

interface AdminBookSeriesPageProps {
  coachId: string;
  memberId: string;
  initialStartDate?: string;
}

export default function AdminBookSeriesPage({
  coachId,
  memberId,
  initialStartDate,
}: AdminBookSeriesPageProps) {
  const currentUserEmail = useAppSelector((state) => state.auth.user?.email ?? '');
  const [visibleMonth, setVisibleMonth] = useState(monthString(initialStartDate));
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedStart, setSelectedStart] = useState('');
  const [enableGemini, setEnableGemini] = useState(true);
  const [bookedSeries, setBookedSeries] = useState<BookSeriesResponse | null>(null);
  const scheduleInFlight = useRef(false);
  const { data, error, isFetching, isLoading } = useGetBookingSeriesOptionsQuery({
    coachPersonId: coachId,
    memberPersonId: memberId,
    month: visibleMonth,
  });
  const [bookSeries, bookSeriesState] = useBookSeriesMutation();
  const selectedSlot = getSlotsForDate(data, selectedDate).find(
    (slot) => slot.start === selectedStart
  );
  const reconnectRequired =
    isReconnectRequiredError(error) || isReconnectRequiredError(bookSeriesState.error);
  const conflictCalendarsUnavailable =
    isConflictCalendarsUnavailableError(error) ||
    isConflictCalendarsUnavailableError(bookSeriesState.error);
  const optionsError = Boolean(error) && !reconnectRequired && !conflictCalendarsUnavailable;

  async function handleSchedule() {
    if (!selectedSlot || scheduleInFlight.current) {
      return;
    }
    scheduleInFlight.current = true;
    try {
      const response = await bookSeries({
        coach_person_id: coachId,
        member_person_id: memberId,
        start: selectedSlot.start,
        enable_gemini_transcripts: enableGemini,
      }).unwrap();
      setBookedSeries(response);
    } catch {
      // RTK Query stores the error in mutation state; keep the UI-driven alert path authoritative.
    } finally {
      scheduleInFlight.current = false;
    }
  }

  useEffect(() => {
    if (!data || selectedDate === '') {
      return;
    }
    if (!data.days.some((day) => day.date === selectedDate)) {
      setSelectedDate('');
      setSelectedStart('');
    }
  }, [data, selectedDate]);

  return (
    <AdminBookSeriesView
      options={data}
      loading={isLoading || isFetching}
      selectedDate={selectedDate}
      selectedStart={selectedStart}
      enableGemini={enableGemini}
      mutationPending={bookSeriesState.isLoading}
      reconnectRequired={reconnectRequired}
      conflictCalendarsUnavailable={conflictCalendarsUnavailable}
      optionsError={optionsError}
      mutationError={bookSeriesState.isError && !reconnectRequired && !conflictCalendarsUnavailable}
      bookedSeries={bookedSeries}
      currentUserEmail={currentUserEmail}
      onSelectDate={(date) => {
        setSelectedDate(date);
        setSelectedStart('');
      }}
      onSelectStart={setSelectedStart}
      onToggleGemini={setEnableGemini}
      onPreviousMonth={() => setVisibleMonth(shiftMonth(visibleMonth, -1))}
      onNextMonth={() => setVisibleMonth(shiftMonth(visibleMonth, 1))}
      onSchedule={handleSchedule}
    />
  );
}

interface AdminBookSeriesViewProps {
  options?: BookingSeriesOptionsResponse;
  loading: boolean;
  selectedDate: string;
  selectedStart: string;
  enableGemini: boolean;
  mutationPending: boolean;
  reconnectRequired: boolean;
  conflictCalendarsUnavailable: boolean;
  optionsError: boolean;
  mutationError: boolean;
  bookedSeries: BookSeriesResponse | null;
  currentUserEmail?: string;
  onSelectDate: (date: string) => void;
  onSelectStart: (start: string) => void;
  onToggleGemini: (enabled: boolean) => void;
  onPreviousMonth: () => void;
  onNextMonth: () => void;
  onSchedule: () => void;
}

export function AdminBookSeriesView({
  options,
  loading,
  selectedDate,
  selectedStart,
  enableGemini,
  mutationPending,
  reconnectRequired,
  conflictCalendarsUnavailable,
  optionsError,
  mutationError,
  bookedSeries,
  currentUserEmail = '',
  onSelectDate,
  onSelectStart,
  onToggleGemini,
  onPreviousMonth,
  onNextMonth,
  onSchedule,
}: AdminBookSeriesViewProps) {
  const dateDays = getDatePickerDays(options, selectedDate);
  const slots = getSlotsForDate(options, selectedDate);
  const canSchedule =
    canScheduleSeries(selectedDate, selectedStart) &&
    !reconnectRequired &&
    !conflictCalendarsUnavailable;

  if (bookedSeries) {
    return (
      <PageShell>
        <Header currentUserEmail={currentUserEmail} />
        <Card sx={bookingCardSx}>
          <CardContent sx={{ p: { xs: 3, md: 6 }, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 54, mb: 2, color: '#1C7C54' }} />
            <Typography variant="h4" sx={{ fontWeight: 750, mb: 1, color: '#2B2118' }}>
              You&apos;re scheduled!
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              A calendar invitation has been sent to the member, coach, and admin.
            </Typography>
            <Stack
              spacing={1.5}
              sx={{
                maxWidth: 520,
                mx: 'auto',
                mb: 4,
                p: 3,
                border: '1px solid #EFE2D0',
                borderRadius: 4,
                textAlign: 'left',
                bgcolor: '#FFFDF9',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                6 Coaching Sessions
              </Typography>
              <Typography>
                {formatRecurringSummary(bookedSeries.start, bookedSeries.timezone)}
              </Typography>
              {bookedSeries.google_meet_link ? (
                <>
                  <Typography>Google Meet</Typography>
                  <Typography color="text.secondary">{bookedSeries.google_meet_link}</Typography>
                </>
              ) : null}
              <Typography>This event is private on the coach&apos;s calendar.</Typography>
              <Typography>
                {bookedSeries.gemini_transcripts_enabled
                  ? 'Gemini transcription is turned on.'
                  : 'Gemini transcription is turned off.'}
              </Typography>
            </Stack>
            <Button variant="contained" href={platformURL('/')} sx={primaryButtonSx}>
              Done
            </Button>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Header currentUserEmail={currentUserEmail} />
      <Card sx={bookingCardSx}>
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: selectedDate ? '1fr 1.1fr 0.8fr' : '1fr 1.2fr',
              },
              minHeight: 520,
            }}
          >
            <Stack
              spacing={3}
              sx={{ p: { xs: 3, md: 4 }, borderRight: { md: '1px solid #EFE2D0' } }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  src={options?.coach.picture_url}
                  sx={{ width: 56, height: 56, bgcolor: '#E8DAFF', color: '#4B2A82' }}
                >
                  {initials(options?.coach.display_name)}
                </Avatar>
                <Box>
                  <Typography color="text.secondary">
                    {options?.coach.display_name || 'Your coach'}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 750 }}>
                    {options?.series.title || options?.title || 'Book Coaching Series'}
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={1.5} color="text.secondary">
                <FactRow icon={<VideocamIcon fontSize="small" />} label="Google Meet" />
                <FactRow
                  icon={<CalendarMonthIcon fontSize="small" />}
                  label={seriesSummary(options, selectedStart)}
                />
                <FactRow
                  icon={<AccessTimeIcon fontSize="small" />}
                  label={`${options?.series.duration_minutes ?? options?.duration_minutes ?? 60} Minutes`}
                />
                <FactRow
                  icon={<PublicIcon fontSize="small" />}
                  label={options?.timezone ?? 'Timezone loading...'}
                />
                <Box sx={{ pl: 4 }}>
                  <Typography sx={{ color: '#3B2F26', fontWeight: 700 }}>
                    {options?.member.display_name || 'Member'}
                  </Typography>
                  <Typography variant="body2">{options?.member.email}</Typography>
                </Box>
              </Stack>

              <FormControlLabel
                control={<Checkbox checked disabled />}
                label="Create 6 session bi-weekly series"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={enableGemini}
                    onChange={(event) => onToggleGemini(event.target.checked)}
                  />
                }
                label="Turn on transcription"
              />
            </Stack>

            <Stack spacing={3} sx={{ p: { xs: 3, md: 4 } }}>
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <IconButton
                    disabled={!options?.window.can_go_previous}
                    onClick={onPreviousMonth}
                    aria-label="Previous month"
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formatMonth(options?.window.month)}
                  </Typography>
                  <IconButton
                    disabled={!options?.window.can_go_next}
                    onClick={onNextMonth}
                    aria-label="Next month"
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Stack>
                <Typography color="text.secondary">
                  Available days reflect all selected conflict calendars.
                </Typography>
              </Box>

              {reconnectRequired ? (
                <Alert severity="warning">
                  Reconnect the coach&apos;s Google Calendar to enable booking.
                </Alert>
              ) : null}
              {conflictCalendarsUnavailable ? (
                <Alert severity="warning">
                  Reconnect the affected selected calendar account and try again.
                </Alert>
              ) : null}
              {optionsError ? (
                <Alert severity="error">Failed to load booking options. Please try again.</Alert>
              ) : null}
              {mutationError && !conflictCalendarsUnavailable ? (
                <Alert severity="error">Failed to schedule this series.</Alert>
              ) : null}
              {options?.warnings.map((warning) => (
                <Alert key={`${warning.code}-${warning.message}`} severity="info">
                  {warning.message}
                </Alert>
              ))}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, minmax(42px, 1fr))',
                    gap: 1,
                  }}
                >
                  {weekdayLabels.map((weekday) => (
                    <Typography
                      key={weekday}
                      variant="caption"
                      sx={{ textAlign: 'center', color: 'text.secondary', fontWeight: 700 }}
                    >
                      {weekday}
                    </Typography>
                  ))}
                  {dateDays.map((day) => (
                    <Button
                      key={day.date}
                      variant={day.selected ? 'contained' : 'outlined'}
                      disabled={!day.enabled}
                      onClick={() => onSelectDate(day.date)}
                      sx={{
                        minWidth: 0,
                        borderRadius: 2,
                        px: 1,
                        py: 1.25,
                        bgcolor: day.date === todayString() ? '#8DAA91' : undefined,
                      }}
                    >
                      {formatCalendarDay(day.date)}
                    </Button>
                  ))}
                </Box>
              )}
              {!selectedDate ? (
                <Button variant="contained" size="large" disabled sx={primaryButtonSx}>
                  Schedule
                </Button>
              ) : null}
            </Stack>

            {selectedDate ? (
              <Stack
                spacing={2}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderLeft: { md: '1px solid #EFE2D0' },
                  bgcolor: '#FFFDF9',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {formatSelectedDate(selectedDate)}
                </Typography>
                <Stack spacing={1}>
                  {slots.map((slot) => (
                    <Button
                      key={slot.start}
                      variant={slot.start === selectedStart ? 'contained' : 'outlined'}
                      onClick={() => onSelectStart(slot.start)}
                      sx={{
                        justifyContent: 'center',
                        borderRadius: 999,
                        bgcolor: slot.start === selectedStart ? undefined : '#FFFFFF',
                      }}
                    >
                      {slot.label}
                    </Button>
                  ))}
                </Stack>

                <Button
                  variant="contained"
                  size="large"
                  disabled={!canSchedule || mutationPending}
                  onClick={onSchedule}
                  sx={primaryButtonSx}
                >
                  {mutationPending ? 'Scheduling...' : 'Schedule'}
                </Button>
              </Stack>
            ) : null}
          </Box>
        </CardContent>
      </Card>
    </PageShell>
  );
}

function Header({ currentUserEmail }: { currentUserEmail: string }) {
  return (
    <Stack spacing={1.5} alignItems="center" sx={{ mb: 4 }}>
      <MentoLogo width={128} height={48} />
      {currentUserEmail ? (
        <Typography variant="body2" color="text.secondary">
          Logged in as {currentUserEmail}
        </Typography>
      ) : null}
    </Stack>
  );
}

function FactRow({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      {icon}
      <Typography>{label}</Typography>
    </Stack>
  );
}

function PageShell({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#FBF5EC',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: { xs: 2, md: 4 },
        py: { xs: 3, md: 7 },
      }}
    >
      {children}
    </Box>
  );
}

const bookingCardSx = {
  width: '100%',
  maxWidth: 1120,
  borderRadius: 5,
  overflow: 'hidden',
  boxShadow: '0 18px 50px rgba(40, 32, 20, 0.12)',
};

const primaryButtonSx = { borderRadius: 999, py: 1.25, px: 4 };

function isReconnectRequiredError(error: unknown): boolean {
  const data = (error as { data?: { code?: string; error?: string } } | undefined)?.data;
  return (
    data?.code === 'google_calendar_reconnect_required' ||
    data?.error === 'google_calendar_reconnect_required'
  );
}

function isConflictCalendarsUnavailableError(error: unknown): boolean {
  const data = (error as { data?: { code?: string; error?: string } } | undefined)?.data;
  return (
    data?.code === 'calendar_conflicts_unavailable' ||
    data?.error === 'calendar_conflicts_unavailable'
  );
}

function formatCalendarDay(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    day: 'numeric',
  });
}

function formatMonth(month?: string): string {
  if (!month) {
    return 'Loading month...';
  }
  const parsed = parseYearMonth(month);
  if (!parsed) {
    return 'Loading month...';
  }
  return new Date(parsed.year, parsed.monthIndex, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
}

function seriesSummary(options?: BookingSeriesOptionsResponse, selectedStart?: string): string {
  if (!options || !selectedStart) {
    return 'Six bi-weekly sessions';
  }
  const recurrenceCount = options.series.recurrence_count ?? 6;
  const recurrenceIntervalWeeks = options.series.recurrence_interval_weeks ?? 2;
  const endDate = new Date(selectedStart);
  endDate.setUTCDate(endDate.getUTCDate() + (recurrenceCount - 1) * recurrenceIntervalWeeks * 7);
  return `Every two weeks through ${endDate.toLocaleDateString(undefined, {
    timeZone: options.timezone,
    month: 'long',
    year: 'numeric',
  })}`;
}

function monthString(value?: string): string {
  const parsed = value ? parseYearMonth(value) : null;
  if (parsed) {
    return formatYearMonth(parsed.year, parsed.monthIndex);
  }
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(month: string, offset: number): string {
  const parsed = parseYearMonth(month);
  if (!parsed) {
    return monthString();
  }
  const monthCount = parsed.year * 12 + parsed.monthIndex + offset;
  const normalizedMonthIndex = ((monthCount % 12) + 12) % 12;
  return formatYearMonth(Math.floor(monthCount / 12), normalizedMonthIndex);
}

function todayString(): string {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
}

function parseYearMonth(value: string): { year: number; monthIndex: number } | null {
  const match = /^(\d{4})-(\d{2})/.exec(value);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return null;
  }
  return { year, monthIndex: month - 1 };
}

function formatYearMonth(year: number, monthIndex: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
}

function initials(value?: string): string {
  const parts = value?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (parts.length === 0) {
    return 'YC';
  }
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

function formatSelectedDate(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

function formatRecurringSummary(value: string, timezone: string): string {
  const start = new Date(value);
  return `Every other ${start.toLocaleDateString(undefined, { timeZone: timezone, weekday: 'long' })} at ${start.toLocaleTimeString(undefined, { timeZone: timezone, hour: 'numeric', minute: '2-digit' })} from ${start.toLocaleDateString(undefined, { timeZone: timezone, month: 'long', day: 'numeric', year: 'numeric' })}`;
}
